import { last } from 'lodash';
import type { BBUiConfig, ComponentTypes, BBInheritanceParent, BBModel, SelectedBBSingle } from 'types/story';
import { COMPONENT_TYPE } from 'common/constants';
import { TimerType } from 'client/components/common/BuildingBlocks/Timer/Timer';
import { AnswerProgressType } from 'client/components/common/BuildingBlocks/AnswerProgress/AnswerProgress';
import {
	BOX_TYPE,
	defaultProps as boxDefaults,
	isHorizontalStack,
	isFlexNowrap,
	BoxType,
	parseAlignItems,
} from 'client/components/common/BuildingBlocks/Box/constants';
import { AnswerInputMode } from 'src/routes/client/components/common/BuildingBlocks/AnswerInput/constants';

// Check if the element can have children components
function canHaveChildren(element: SelectedBBSingle | BBModel): boolean {
	return element.uiConfig.editorProps.expandable;
}

const getParent = (parents: BBInheritanceParent[] | undefined) => last(parents);

const isParentBoxType = (parents: BBInheritanceParent[], boxType: BoxType) => {
	const parent = getParent(parents);
	if (!parent || parent.type !== COMPONENT_TYPE.BOX) {
		return false;
	}
	const parentBoxType = parent?.inheritance.componentProps?.other?.boxType;
	return parentBoxType ? (last(parentBoxType)!.value as BoxType) === boxType : false;
};

const isParentAllowChangeHeight = (parents: BBInheritanceParent[] | undefined) => {
	const parent = getParent(parents);
	if (!parent || parent.type !== COMPONENT_TYPE.BOX) return true;

	const parentProps = parent.inheritance.componentProps;
	const boxType: BoxType | undefined = last(parentProps?.other?.boxType)?.value;

	if (boxType === BOX_TYPE.STACK) {
		const direction = last(parentProps?.styles?.flexDirection)?.value ?? boxDefaults.flexDirection;
		if (isHorizontalStack(direction, boxType)) {
			const alignX = last(parentProps?.styles?.alignItems)?.value ?? boxDefaults.alignItems;
			if (alignX === 'stretch') return false;
		}

		return true;
	}

	return boxType === BOX_TYPE.FRAME || !boxType;
};

// Whether building block height can be changed
const canChangeHeight = (type: ComponentTypes, uiConfig: BBUiConfig, parents: BBInheritanceParent[] | undefined) => {
	const parentAllowChangeHeight = isParentAllowChangeHeight(parents);
	const alignSelf = uiConfig.componentProps.styles?.alignSelf;
	const height = uiConfig.componentProps.styles?.height;

	if (alignSelf === 'stretch' || height === 'auto' || !parentAllowChangeHeight) {
		return false;
	}

	switch (type) {
		case COMPONENT_TYPE.TEXT:
		case COMPONENT_TYPE.RESULT_TEXT:
		case COMPONENT_TYPE.CHECKBOX:
		case COMPONENT_TYPE.COUNTER:
		case COMPONENT_TYPE.SLIDER_SLIDES:
			return false;
		case COMPONENT_TYPE.ANSWER_INPUT:
			return uiConfig.componentProps.answerInputMode === AnswerInputMode.Word;
		case COMPONENT_TYPE.ANSWER_PROGRESS:
			return uiConfig.componentProps.answerProgressType === AnswerProgressType.bar;
		case COMPONENT_TYPE.BOX: {
			const boxType = uiConfig.componentProps.other?.boxType;
			if (isHorizontalStack(uiConfig.componentProps.styles.flexDirection, boxType)) {
				return true;
			}
			return boxType === BOX_TYPE.FRAME || !boxType;
		}
		case COMPONENT_TYPE.TIMER: {
			return uiConfig.componentProps.timerType === TimerType.bar;
		}
		default:
			return true;
	}
};

const isParentAllowChangeWidth = (parents: BBInheritanceParent[] | undefined) => {
	const parent = getParent(parents);
	if (!parent || !([COMPONENT_TYPE.BOX, COMPONENT_TYPE.SORTABLE_BOX] as ComponentTypes[]).includes(parent.type)) {
		return true;
	}

	const parentProps = parent.inheritance.componentProps;
	const boxType: BoxType | undefined = last(parentProps?.other?.boxType)?.value ?? boxDefaults.boxType;

	if (boxType === BOX_TYPE.STACK) {
		const direction = last(parentProps?.styles?.flexDirection)?.value ?? boxDefaults.flexDirection;
		if (isHorizontalStack(direction, boxType)) return true;

		const alignX = parseAlignItems(last(parentProps?.styles?.alignItems)?.value ?? boxDefaults.alignItems);
		return alignX === 'start' || alignX === 'end' || alignX === 'center';
	}

	return boxType === BOX_TYPE.FRAME || !boxType;
};

// Whether building block width can be changed
const canChangeWidth = (type: ComponentTypes, uiConfig: BBUiConfig, parents: BBInheritanceParent[] | undefined) => {
	const parentAllowChangeWidth = isParentAllowChangeWidth(parents);
	const justifySelf = uiConfig.componentProps.styles?.justifySelf;
	const width = uiConfig.componentProps.styles?.width;

	if (justifySelf === 'stretch' || width === 'auto' || !parentAllowChangeWidth) {
		return false;
	}

	switch (type) {
		case COMPONENT_TYPE.TIMER: {
			return uiConfig.componentProps?.timerType === TimerType.bar;
		}
		case COMPONENT_TYPE.ANSWER_PROGRESS: {
			return uiConfig.componentProps.answerProgressType === AnswerProgressType.bar;
		}
		case COMPONENT_TYPE.COUNTER:
		case COMPONENT_TYPE.SLIDER_SLIDES: {
			return false;
		}
		case COMPONENT_TYPE.BOX: {
			const boxType = uiConfig.componentProps.other?.boxType;
			const flexWrap = uiConfig.componentProps.styles?.flexWrap;
			const flexDirection = uiConfig.componentProps.styles?.flexDirection;
			return !(isHorizontalStack(flexDirection, boxType) && isFlexNowrap(flexWrap));
		}
		default:
			return true;
	}
};

export { getParent, isParentBoxType, canChangeHeight, canChangeWidth, canHaveChildren };
